<template>
    <div
        ref="cellRef"
        class="ds-month-cell ds-timesheet-cell"
        :class="{
            'ds-month-cell-interactive': canEdit,
            'ds-month-cell-active': isHovered && hasDetails,
            'ds-month-cell-today': isToday,
        }"
        :style="mode === 'dayState' ? { backgroundColor: dayStateColor } : undefined"
        :data-schedule-date="date"
        :tabindex="hasDetails ? 0 : undefined"
        :aria-describedby="isTooltipOpen ? tooltipId : undefined"
        @mouseenter="onPointerEnter"
        @mouseleave="onPointerLeave"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.esc="onEscape"
    >
        <div v-if="stripe" class="ds-month-cell-stripe" :class="STRIPE_CLASSES[stripe]" />

        <template v-if="mode === 'work'">
            <div v-if="hasAdditional" class="ds-timesheet-month-body">
                <span class="ds-timesheet-month-total">{{ factText }}</span>
                <span class="ds-timesheet-month-main">{{ mainText }}</span>
                <span class="ds-timesheet-month-extra">{{ additionalText }}</span>
            </div>
            <div v-else class="ds-timesheet-month-body">
                <span class="ds-timesheet-month-total">{{ factText }}</span>
            </div>
        </template>

        <div v-else-if="mode === 'dayState' || mode === 'code'" class="ds-timesheet-month-body">
            <span class="ds-timesheet-month-code">{{ mode === 'dayState' ? dayStateShortName : shortName }}</span>
            <span v-if="showCodeHours" class="ds-timesheet-month-total">{{ factText }}</span>
            <!-- КС + доп. смена (поручение Романа 19.10.2026): плашка доп. часов или код неявки доп. смены. -->
            <span v-if="dayStateExtra?.kind === 'additional'" class="ds-timesheet-month-extra">{{ dayStateExtra.text }}</span>
            <span v-else-if="dayStateExtra?.kind === 'absence'" class="ds-timesheet-month-code">{{ dayStateExtra.code }}</span>
        </div>

        <div v-else-if="mode === 'absence'" class="ds-timesheet-month-body">
            <span class="ds-timesheet-month-code">{{ shortName }}</span>
        </div>

        <div v-else-if="mode === 'dayoff'" class="ds-month-cell-label">ВЫХ</div>

        <div v-if="isToday" class="ds-week-cell-today" aria-hidden="true" />

        <TimesheetMonthTooltip
            v-if="isTooltipOpen && cellRef"
            :id="tooltipId"
            :anchor="cellRef"
            :title="tooltipTitle"
            :day-state="tooltipDayState"
            :rows="tooltipRows"
            :shifts="tooltipShifts"
            :total="mode === 'work' ? factText : ''"
            :chips="tooltipChips"
        />
    </div>
</template>

<script lang="ts">
import { shallowRef } from 'vue'

// Одновременно открыта одна подсказка табеля: новая ячейка закрывает прежнюю.
const activeTooltipOwner = shallowRef<symbol | null>(null)
let tooltipSequence = 0
</script>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import type { Schedule } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import TimesheetMonthTooltip, { type TimesheetTooltipRow } from './TimesheetMonthTooltip.vue'
import { formatTimesheetSeconds, formatTimesheetShiftTime, useTimesheetCell } from './timesheetCell'

interface Props {
    schedule: Schedule
    date: string
}

const TOOLTIP_DELAY = 500
// Полные имена классов: Tailwind вырезает из @layer components классы, собранные шаблонной строкой.
const STRIPE_CLASSES = { 'warning': 'ds-month-cell-stripe-warning', 'no-marks': 'ds-month-cell-stripe-no-marks' } as const

const props = defineProps<Props>()

const authStore = useAuthStore()
const {
    day, isToday, mode, timesheetDay, totals, shortName, fullName, dayStateName, dayStateShortName, dayStateColor,
    hasAdditional, hasFact, factText, mainText, additionalText, shiftTimings, hasViolation, violationMessages, stripe,
    isDayStateShift, dayStateExtra,
} = useTimesheetCell(props)

const cellRef = ref<HTMLElement | null>(null)
const owner = Symbol('timesheet-month-tooltip')
const tooltipId = `timesheet-month-tooltip-${++tooltipSequence}`
const isHovered = ref(false)
const isFocused = ref(false)
let tooltipTimer: ReturnType<typeof setTimeout> | undefined

// Клик открывает прежнюю панель смены по праву can_edit_timesheet (проверка в Schedule.vue).
const canEdit = computed(() => Boolean(authStore.permissions?.can_edit_timesheet))

// Код без часов не получает фиктивный ноль: часы — только если они есть в ответе и больше нуля (кадровое состояние — всегда из ответа).
const showCodeHours = computed(() => hasFact.value && (mode.value === 'dayState' || (totals.value.fact_seconds ?? 0) > 0))

const hasDetails = computed(() => ['work', 'dayState', 'absence', 'code'].includes(mode.value))
const isTooltipOpen = computed(() => hasDetails.value && activeTooltipOwner.value === owner)

const tooltipTitle = computed(() => {
    const title = day.value.format('dd, D MMMM')
    return title.charAt(0).toUpperCase() + title.slice(1)
})

const tooltipDayState = computed(() => {
    if (mode.value === 'dayState') return { name: dayStateName.value, color: dayStateColor.value }
    if (mode.value === 'absence' || mode.value === 'code') return { name: fullName.value, color: 'var(--color-bg-muted)' }
    return null
})

// Готовые поля ответа: основные, доп., ночные «в том числе» (к итогу не прибавляются), итог — fact_seconds.
const tooltipRows = computed<TimesheetTooltipRow[]>(() => {
    const value = totals.value
    const rows: TimesheetTooltipRow[] = []
    if (mode.value !== 'work') {
        if (hasFact.value) rows.push({ label: 'Учтено часов', value: factText.value })
        return rows
    }
    if (typeof value.main_seconds === 'number') rows.push({ label: 'Основные часы', value: mainText.value })
    if (typeof value.additional_seconds === 'number') {
        rows.push(hasAdditional.value
            ? { label: 'Доп. табель', value: additionalText.value, extra: true }
            : { label: 'Доп. табель', value: formatTimesheetSeconds(value.additional_seconds) })
    }
    if (typeof value.fact_night_seconds === 'number') {
        rows.push({ label: 'в т.ч. ночных', value: formatTimesheetSeconds(value.fact_night_seconds), nested: true })
    }
    return rows
})

// Все смены дня из табеля: должность и время (факт, иначе план — как в прежней подсказке табеля).
// Запись любого КС без валидного диапазона (r2) не выводится: её название и часы уже в подсказке.
// Рабочие смены показываются всегда.
const tooltipShifts = computed(() => (timesheetDay.value?.shifts ?? [])
    .flatMap((shift, index) => {
        const start = shift.fact_start || shift.plan_start
        const end = shift.fact_end || shift.plan_end
        const startText = formatTimesheetShiftTime(start)
        const endText = formatTimesheetShiftTime(end)
        const hasRange = startText !== '??:??' && endText !== '??:??' && startText !== endText
        if (isDayStateShift(shift) && !hasRange) return []
        return [{
            key: shift.id ?? String(index),
            start: start ? dayjs(start).valueOf() : Number.POSITIVE_INFINITY,
            text: `${shift.position_name || '—'}: ${startText}–${endText}`,
        }]
    })
    .sort((a, b) => a.start - b.start)
    .map(({ key, text }) => ({ key, text })))

// Нарушения — тексты backend; «Без нарушений» только при загруженных данных графика и завершённых сменах.
const tooltipChips = computed(() => {
    if (hasViolation.value) {
        const messages = violationMessages.value.length ? violationMessages.value : ['Нарушение']
        return [...new Set(messages)].map(text => ({ text, kind: 'violation' as const }))
    }
    if (mode.value === 'work' && shiftTimings.value.some(timing => !timing.isUpcoming && !timing.isInProgress && !timing.isNoMarks)) {
        return [{ text: 'Без нарушений', kind: 'ok' as const }]
    }
    return []
})

function clearTooltipTimer() {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    tooltipTimer = undefined
}

function openTooltip() {
    clearTooltipTimer()
    if (hasDetails.value) activeTooltipOwner.value = owner
}

function closeTooltip() {
    clearTooltipTimer()
    if (activeTooltipOwner.value === owner) activeTooltipOwner.value = null
}

function onPointerEnter() {
    isHovered.value = true
    if (!hasDetails.value) return
    clearTooltipTimer()
    tooltipTimer = setTimeout(openTooltip, TOOLTIP_DELAY)
}

function onPointerLeave() {
    isHovered.value = false
    if (!isFocused.value) closeTooltip()
}

// Клавиатурный фокус сразу показывает подсказку; фокус от клика мыши её не открывает.
function onFocus(event: FocusEvent) {
    isFocused.value = true
    const target = event.currentTarget
    if (target instanceof HTMLElement && target.matches(':focus-visible')) openTooltip()
}

function onBlur() {
    isFocused.value = false
    if (!isHovered.value) closeTooltip()
}

function onEscape(event: KeyboardEvent) {
    if (!isTooltipOpen.value) return
    event.stopPropagation()
    closeTooltip()
}

// Подсказка не переживает прокрутку/изменение размеров: её позиция привязана к ячейке.
function onViewportChange() {
    closeTooltip()
}

watch(isTooltipOpen, (open) => {
    if (open) {
        window.addEventListener('scroll', onViewportChange, true)
        window.addEventListener('resize', onViewportChange)
    } else {
        window.removeEventListener('scroll', onViewportChange, true)
        window.removeEventListener('resize', onViewportChange)
    }
})

watch(hasDetails, (value) => {
    if (!value) closeTooltip()
})

onUnmounted(() => {
    closeTooltip()
    window.removeEventListener('scroll', onViewportChange, true)
    window.removeEventListener('resize', onViewportChange)
})
</script>
